import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';
import TeamworkButton from './TeamworkButton';
import NotificationService from '../services/notificationService';
import TeamworkLiveToast from './TeamworkLiveToast';
import { prepareBrowserNotifications, showTeamworkBrowserNotification } from '../utils/browserNotifications';
import { subscribePush } from '../utils/webPushClient';

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  height: 64px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  color: #475569;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f1f5f9; color: #0f172a; }
`;

const Dropdown = styled.div`
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 224px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1);
`;

const Row = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid #e2e8f0;
`;

const MenuBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #334155;
  &:hover { background: #f1f5f9; }
`;

const RoleBadge = styled.span`
  display: inline-block;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #cbd5e1;
`;

const normalizeImagePath = (imagePath) => {
  try {
    if (!imagePath) return undefined;
    let cleaned = String(imagePath).trim().replace(/\\/g, '/').replace(/^\/+/, '');
    cleaned = cleaned.replace(/^staff_images\//, '').replace(/^\/staff_images\//, '');
    const parts = cleaned.split('/');
    const filename = parts[parts.length - 1];
    return filename || undefined;
  } catch { return undefined; }
};

export default function RecoveryTopBar({ title, onMenuClick }) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [teamworkCount, setTeamworkCount] = useState(0);
  const [teamworkOverdue, setTeamworkOverdue] = useState(false);
  const stored = AuthService.getUser();
  const teamworkSocketUserId = stored?.superAdmin?.superAdminId || stored?.superAdmin?.staff?.staffId;
  const teamworkSocketUserType = stored?.superAdmin?.superAdminId ? 'super_admin' : 'staff';
  const currentStaffId = stored?.superAdmin?.staff?.staffId || null;
  const currentSuperAdminId = stored?.superAdmin?.superAdminId || null;

  const loadTeamworkCount = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/staff-teamwork/count').catch(() => null);
      if (res?.data?.count !== undefined) {
        setTeamworkCount(res.data.count);
        setTeamworkOverdue((res.data.overdueCount || 0) > 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadTeamworkCount();
    const interval = setInterval(loadTeamworkCount, 60000);
    return () => clearInterval(interval);
  }, [loadTeamworkCount]);

  useEffect(() => {
    window.addEventListener('ka-teamwork-count-changed', loadTeamworkCount);
    const storageHandler = (e) => { if (e.key === 'ka_tw_refresh') loadTeamworkCount(); };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('ka-teamwork-count-changed', loadTeamworkCount);
      window.removeEventListener('storage', storageHandler);
    };
  }, [loadTeamworkCount]);

  useEffect(() => {
    if (!teamworkSocketUserId) return;
    NotificationService.connect(
      teamworkSocketUserId,
      teamworkSocketUserType,
      stored?.superAdmin?.name || stored?.superAdmin?.staff?.name || '',
      stored?.superAdmin?.staff?.staffId || null
    );
    prepareBrowserNotifications();
    subscribePush(teamworkSocketUserId, 'admin');
    const offCount = NotificationService.onTeamworkCountUpdated(loadTeamworkCount);
    const offMessage = NotificationService.onTeamworkMessageAlert((payload = {}) => {
      loadTeamworkCount();

      const isSelfSender =
        (payload?.senderType === 'staff' && Number(payload?.senderStaffId) === Number(currentStaffId)) ||
        (payload?.senderType === 'super_admin' && Number(payload?.senderSuperAdminId) === Number(currentSuperAdminId));
      if (isSelfSender) return;

      const title = payload?.title || (payload?.teamworkCode
        ? `${payload.teamworkCode} - New Teamwork Message`
        : 'New Teamwork Message');
      const body = payload?.body || payload?.messagePreview || 'You received a new teamwork message.';
      window.dispatchEvent(new CustomEvent('ka-teamwork-live-alert', {
        detail: {
          title,
          body,
          link: payload?.teamworkId ? `/admin/teamwork?teamworkId=${encodeURIComponent(payload.teamworkId)}` : '/admin/teamwork',
        },
      }));

      showTeamworkBrowserNotification(payload);
    });
    return () => {
      offCount();
      offMessage();
      NotificationService.disconnect();
    };
  }, [teamworkSocketUserId, teamworkSocketUserType, loadTeamworkCount, currentStaffId, currentSuperAdminId]);
  const [avatarOverride, setAvatarOverride] = useState(undefined);
  const [avatarError, setAvatarError] = useState(false);

  const staffType = String(stored?.superAdmin?.staff?.staffType || '').toLowerCase().replace(/\s+/g, '');
  const isManagementUser = staffType === 'management' || staffType === 'manager';
  const isAdminUser = !!stored?.superAdmin?.superAdminId || stored?.superAdmin?.staff?.type === 'admin' || isManagementUser;
  const [hasAdminDashboardAccess, setHasAdminDashboardAccess] = useState(isAdminUser);

  useEffect(() => {
    const handler = (evt) => {
      try {
        const detail = evt?.detail || {};
        const filename = normalizeImagePath(detail.filename || undefined);
        if (!filename) return;
        setAvatarOverride(`/storage/staff_images/${encodeURIComponent(filename)}`);
        setAvatarError(false);
      } catch (e) {}
    };
    window.addEventListener('ka-user-avatar-updated', handler);
    return () => window.removeEventListener('ka-user-avatar-updated', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      try {
        if (!stored) {
          setHasAdminDashboardAccess(false);
          return;
        }
        if (isAdminUser) {
          setHasAdminDashboardAccess(true);
          return;
        }
        const resp = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (cancelled || !resp || !resp.data) return;

        const data = resp.data || {};
        const coordinateCourses = data.coordinateCourses || {};
        const accessibleCourseIds = Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : [];
        const isSuperAdmin = Boolean(data.isSuperAdmin);
        const hasAnyCoordinateCourses = Object.keys(coordinateCourses).length > 0 || accessibleCourseIds.length > 0;

        setHasAdminDashboardAccess(isAdminUser || isSuperAdmin || hasAnyCoordinateCourses);
      } catch (_) {
        if (!cancelled) setHasAdminDashboardAccess(isAdminUser);
      }
    };

    loadAccess();
    return () => { cancelled = true; };
  }, [isAdminUser, stored]);

  const getUserInfo = () => {
    if (!stored || !stored.superAdmin) {
      return { name: 'Recovery Officer', email: '', role: 'Recovery', avatar: undefined };
    }
    const staff = stored.superAdmin.staff;
    const rawPath = staff?.imagePath;
    let avatarPath = undefined;
    if (rawPath) {
      if (String(rawPath).startsWith('/')) {
        avatarPath = rawPath;
      } else {
        const filename = normalizeImagePath(rawPath);
        avatarPath = filename ? `/storage/staff_images/${encodeURIComponent(filename)}` : undefined;
      }
    }
    return {
      name: staff?.name || 'Recovery Officer',
      email: staff?.officeEmail || staff?.gmail || '',
      role: staff?.type || 'Recovery',
      avatar: avatarPath,
    };
  };

  const user = getUserInfo();

  const handleLogout = async () => {
    try { await axiosInstance.post('/logout'); } catch {}
    AuthService.clear();
    window.location.href = '/';
  };

  const roleColors = {
    Recovery: { bg: 'linear-gradient(90deg,#fee2e2,#fecaca)', color: '#7f1d1d', border: '#fca5a5' },
    Admin:    { bg: 'linear-gradient(90deg,#f3e8ff,#fce7f3)', color: '#6b21a8', border: '#d8b4fe' },
  };
  const rc = roleColors[user.role] || { bg: 'linear-gradient(90deg,#f3f4f6,#e2e8f0)', color: '#1f2937', border: '#cbd5e1' };

  return (
    <Header>
      <Left>
        <IconButton onClick={onMenuClick} aria-label="Toggle menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </IconButton>
        {title && <h1 style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>{title}</h1>}
      </Left>
      <Right>
        <TeamworkLiveToast />
        {/* Teamwork Icon Button */}
        <TeamworkButton count={teamworkCount} overdue={teamworkOverdue} onClick={() => window.open('/admin/teamwork', '_blank')} />
        <div style={{ position: 'relative' }}>
          <IconButton
            onClick={() => setShowUserMenu((v) => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}
          >
            {(() => {
              const srcPath = avatarOverride || user.avatar;
              if (!srcPath || avatarError) {
                const initials = (user.name || 'U')
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase();
                return (
                  <div
                    style={{
                      height: 32,
                      width: 32,
                      borderRadius: '9999px',
                      background: '#dc2626',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {initials}
                  </div>
                );
              }
              return (
                <img
                  src={getImageURL(srcPath)}
                  alt={user.name || 'User'}
                  style={{ height: 32, width: 32, borderRadius: '9999px', objectFit: 'cover' }}
                  onError={() => setAvatarError(true)}
                />
              );
            })()}
            <span style={{ marginLeft: 8, fontSize: 14, color: '#374151', fontWeight: 500 }}>
              {user.name || 'User'}
            </span>
            <svg style={{ marginLeft: 4 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </IconButton>

          {showUserMenu && (
            <Dropdown>
              <Row>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{user.name}</p>
                <p style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>{user.email}</p>
                <RoleBadge style={{ marginTop: 8, background: rc.bg, color: rc.color, borderColor: rc.border }}>
                  {user.role}
                </RoleBadge>
              </Row>
              <div>
                <MenuBtn onClick={() => (window.location.href = '/recovery/profile')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Your Profile
                </MenuBtn>
                {hasAdminDashboardAccess && (
                  <MenuBtn onClick={() => (window.location.href = '/admin/dashboard')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12h4l3 8 4-16 3 8h4" />
                    </svg>
                    View Admin Dashboard
                  </MenuBtn>
                )}
              </div>
              <div style={{ borderTop: '1px solid #e2e8f0' }}>
                <MenuBtn onClick={handleLogout} style={{ color: '#dc2626' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </MenuBtn>
              </div>
            </Dropdown>
          )}
        </div>
      </Right>
    </Header>
  );
}
